import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCart } from '../hooks/useCart';
import { apiCall } from '../lib/api';
import { trackBeginCheckout, trackUseCoupon } from '../lib/analytics';
import type { CreateCheckoutOutput, CustomerCoupon } from '@meso/types';

interface CouponsResp {
  available: any[];
  claimed: CustomerCoupon[];
}

export function Cart() {
  const cart = useCart();
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedCouponId, setSelectedCouponId] = useState<string | null>(null);

  // โหลดคูปองที่ลูกค้ารับไว้แล้ว — แสดงเฉพาะที่ status=claimed + ยังไม่หมดอายุ
  const coupons = useQuery<CouponsResp>({
    queryKey: ['coupons', 'me'],
    queryFn: () => apiCall<CouponsResp>('get', '/coupons'),
    staleTime: 60_000,
  });
  // คูปองทั้งหมดที่ยัง "claimed" และยังไม่หมดอายุ (รวมที่ยังไม่ผูก discount code — เพื่อ visibility)
  const claimedActive = useMemo(() => {
    const now = Date.now();
    return (coupons.data?.claimed ?? []).filter((c) => {
      if (c.status !== 'claimed') return false;
      if (c.expires_at && new Date(c.expires_at).getTime() < now) return false;
      return true;
    });
  }, [coupons.data]);
  // เฉพาะที่ใช้ได้จริง (มี discount code + ผ่านขั้นต่ำ)
  const usableCoupons = useMemo(
    () => claimedActive.filter((c) =>
      !!c.discount_code &&
      (c.min_purchase == null || cart.total >= Number(c.min_purchase)),
    ),
    [claimedActive, cart.total],
  );

  const selectedCoupon = useMemo(
    () => usableCoupons.find((c) => c.id === selectedCouponId) ?? null,
    [usableCoupons, selectedCouponId],
  );

  // ถ้าลูกค้าเลือกคูปองแล้ว ลบสินค้าจนยอดต่ำกว่าขั้นต่ำ → auto-deselect
  useEffect(() => {
    if (selectedCouponId && !selectedCoupon) setSelectedCouponId(null);
  }, [selectedCouponId, selectedCoupon]);

  // คำนวณส่วนลดประมาณการณ์ (Shopify เป็นผู้คำนวณจริง — นี่แค่ preview)
  const discountPreview = useMemo(() => {
    if (!selectedCoupon) return 0;
    if (selectedCoupon.unit === 'percent') {
      return Math.round(cart.total * Number(selectedCoupon.value) / 100);
    }
    return Math.min(cart.total, Number(selectedCoupon.value));
  }, [selectedCoupon, cart.total]);
  const finalTotal = Math.max(0, cart.total - discountPreview);

  const checkout = useMutation({
    mutationFn: () => apiCall<CreateCheckoutOutput>('post', '/shop/checkout', {
      items: cart.items.map((i) => ({ variantId: i.variantId, quantity: i.quantity })),
      couponId: selectedCouponId ?? undefined,
    }),
    onSuccess: (data) => {
      const total = finalTotal;
      const itemCount = cart.count;
      trackBeginCheckout(
        cart.items.map((i) => ({
          item_id: i.variantId,
          item_name: i.title,
          price: i.price,
          quantity: i.quantity,
          item_brand: 'mesoestetic',
        })),
        total,
      );
      if (data.discountCode) trackUseCoupon(data.discountCode, discountPreview);
      cart.clear();
      navigate('/shop/redirecting', {
        replace: true,
        state: { checkoutUrl: data.checkoutUrl, total, itemCount },
      });
    },
  });

  if (cart.items.length === 0) {
    return (
      <div className="max-w-md mx-auto px-4 py-6">
        <h1 className="text-xl font-bold mb-4">ตะกร้าสินค้า</h1>
        <div className="card text-center py-12">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
               className="w-16 h-16 mx-auto mb-3 text-gray-300">
            <path strokeLinecap="round" strokeLinejoin="round"
                  d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"/>
          </svg>
          <div className="text-gray-500 mb-4">ตะกร้าว่างเปล่า</div>
          <Link to="/shop" className="btn btn-primary">เลือกสินค้า</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-6 pb-44">
      <h1 className="text-xl font-bold mb-4">ตะกร้าสินค้า</h1>

      <div className="space-y-3">
        {cart.items.map((item) => (
          <div key={item.variantId} className="card flex gap-3 items-center">
            {item.image && <img src={item.image} className="w-16 h-16 rounded-lg object-cover" alt="" />}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold line-clamp-2">{item.title}</div>
              <div className="text-sm font-bold mt-1">
                ฿{item.price.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <button onClick={() => cart.updateQty(item.variantId, item.quantity - 1)}
                        className="w-7 h-7 border border-gray-300 rounded-md text-sm">−</button>
                <span className="w-8 text-center text-sm">{item.quantity}</span>
                <button onClick={() => cart.updateQty(item.variantId, item.quantity + 1)}
                        className="w-7 h-7 border border-gray-300 rounded-md text-sm">+</button>
                <button onClick={() => cart.remove(item.variantId)}
                        className="ml-auto text-xs text-red-500">ลบ</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Coupon picker row */}
      <button
        onClick={() => setPickerOpen(true)}
        className="card mt-3 w-full text-left flex items-center gap-3 active:bg-gray-50 transition"
      >
        <span className="w-10 h-10 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center flex-shrink-0">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6Z" />
          </svg>
        </span>
        <div className="flex-1 min-w-0">
          {selectedCoupon ? (
            <>
              <div className="text-sm font-semibold text-rose-700 truncate">{selectedCoupon.title}</div>
              <div className="text-[11px] text-gray-500 truncate">
                code: <span className="font-mono">{selectedCoupon.discount_code}</span>
              </div>
            </>
          ) : (
            <>
              <div className="text-sm font-semibold">ใช้คูปอง</div>
              <div className="text-[11px] text-gray-500">
                {usableCoupons.length > 0
                  ? `มี ${usableCoupons.length} คูปองพร้อมใช้`
                  : claimedActive.length > 0
                    ? `มี ${claimedActive.length} คูปอง — แต่ยอดยังไม่ถึงเกณฑ์ขั้นต่ำ`
                    : 'ยังไม่มีคูปอง — ไปรับคูปองที่ /coupons ก่อน'}
              </div>
            </>
          )}
        </div>
        {selectedCoupon ? (
          <span
            onClick={(e) => { e.stopPropagation(); setSelectedCouponId(null); }}
            className="text-[11px] text-red-500 underline px-2 py-1"
          >
            ลบ
          </span>
        ) : (
          <span className="text-sm text-gray-400">›</span>
        )}
      </button>

      {/* Summary card */}
      <div className="card mt-3 space-y-1.5">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">ยอดรวมก่อนส่วนลด</span>
          <span>฿{cart.total.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
        </div>
        {selectedCoupon && (
          <div className="flex justify-between text-sm text-rose-600">
            <span>ส่วนลดประมาณการ ({selectedCoupon.unit === 'percent' ? `${selectedCoupon.value}%` : 'บาท'})</span>
            <span>−฿{discountPreview.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
          </div>
        )}
        <div className="flex justify-between pt-1 border-t">
          <span className="text-sm font-semibold">ยอดสุทธิ</span>
          <span className="font-bold text-lg">
            ฿{finalTotal.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
          </span>
        </div>
        {selectedCoupon && (
          <p className="text-[10px] text-gray-400">
            * ส่วนลดที่แสดงเป็นการประมาณการณ์ — Shopify จะคำนวณยอดจริงตอนชำระเงิน
          </p>
        )}
      </div>

      <div className="fixed bottom-16 left-0 right-0 p-3 bg-white border-t border-gray-200 z-40">
        <div className="max-w-md mx-auto">
          {checkout.isError && (
            <div className="form-error text-center mb-2">{(checkout.error as Error).message}</div>
          )}
          <button onClick={() => checkout.mutate()} disabled={checkout.isPending}
                  className="btn btn-primary btn-block">
            {checkout.isPending ? 'กำลังสร้าง checkout...' : `ดำเนินการสั่งซื้อ · ฿${finalTotal.toLocaleString('th-TH')}`}
          </button>
          <p className="text-[11px] text-center text-gray-400 mt-2">
            ระบบจะ auto-login เข้า Shopify {selectedCoupon ? '· คูปองจะถูกใช้อัตโนมัติ' : ''}
          </p>
        </div>
      </div>

      {/* Coupon picker bottom sheet */}
      {pickerOpen && (
        <CouponPicker
          coupons={claimedActive}
          cartTotal={cart.total}
          selectedId={selectedCouponId}
          onPick={(id) => { setSelectedCouponId(id); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Bottom sheet coupon picker ────────────────────────────────
function CouponPicker({ coupons, cartTotal, selectedId, onPick, onClose }: {
  coupons: CustomerCoupon[];
  cartTotal: number;
  selectedId: string | null;
  onPick: (id: string | null) => void;
  onClose: () => void;
}) {
  // Categorize: blocker tells UX why coupon can't be used (if any)
  function disabledReason(c: CustomerCoupon): string | null {
    if (!c.discount_code) return 'admin ยังไม่ได้ผูก Shopify code';
    if (c.min_purchase != null && cartTotal < Number(c.min_purchase)) {
      const need = Number(c.min_purchase) - cartTotal;
      return `ต้องสั่งเพิ่ม ฿${need.toLocaleString('th-TH', { minimumFractionDigits: 2 })} (ขั้นต่ำ ฿${Number(c.min_purchase).toLocaleString('th-TH')})`;
    }
    return null;
  }

  // Sort: usable first, then by value desc
  const sorted = useMemo(() => {
    const arr = [...coupons];
    arr.sort((a, b) => {
      const aDis = !!disabledReason(a);
      const bDis = !!disabledReason(b);
      if (aDis !== bDis) return aDis ? 1 : -1;
      return Number(b.value) - Number(a.value);
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupons, cartTotal]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="bg-white rounded-t-2xl px-4 py-5 max-h-[75vh] overflow-y-auto">
        <div className="flex items-center mb-3">
          <h2 className="text-lg font-bold flex-1">เลือกคูปองที่จะใช้</h2>
          <button onClick={onClose} className="text-gray-400 text-2xl leading-none">×</button>
        </div>

        {sorted.length === 0 && (
          <div className="text-center text-gray-500 py-10">
            <div className="text-3xl mb-2">🎟️</div>
            <div className="text-sm">ยังไม่มีคูปองที่ใช้ได้</div>
            <Link to="/coupons" onClick={onClose}
                  className="btn btn-primary mt-4 inline-block">ไปรับคูปอง</Link>
          </div>
        )}

        <div className="space-y-2">
          {sorted.map((c) => {
            const reason = disabledReason(c);
            const isDisabled = !!reason;
            const isSelected = selectedId === c.id;
            return (
              <button
                key={c.id}
                onClick={() => !isDisabled && onPick(c.id)}
                disabled={isDisabled}
                className={`w-full text-left card flex items-center gap-3 transition
                  ${isSelected ? 'ring-2 ring-rose-500 bg-rose-50' : ''}
                  ${isDisabled ? 'opacity-60 cursor-not-allowed' : 'active:bg-gray-50'}`}
              >
                <div className={`w-14 h-14 rounded-lg flex flex-col items-center justify-center flex-shrink-0
                  ${isDisabled ? 'bg-gray-100 text-gray-400' : 'bg-rose-100 text-rose-700'}`}>
                  <div className="text-lg font-bold leading-none">{c.value}</div>
                  <div className="text-[10px]">{c.unit === 'percent' ? '%' : 'บาท'}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{c.title}</div>
                  {isDisabled ? (
                    <div className="text-[11px] text-amber-600 mt-0.5 font-medium">
                      ⚠️ {reason}
                    </div>
                  ) : (
                    <>
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        code: <span className="font-mono">{c.discount_code}</span>
                      </div>
                      {c.min_purchase != null && Number(c.min_purchase) > 0 && (
                        <div className="text-[10px] text-emerald-600 mt-0.5">
                          ✓ ผ่านขั้นต่ำ ฿{Number(c.min_purchase).toLocaleString('th-TH')}
                        </div>
                      )}
                    </>
                  )}
                  {c.expires_at && (
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      หมดอายุ {new Date(c.expires_at).toLocaleDateString('th-TH')}
                    </div>
                  )}
                </div>
                {isSelected && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                       className="w-5 h-5 text-rose-500">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
                  </svg>
                )}
              </button>
            );
          })}
        </div>

        {selectedId && (
          <button onClick={() => onPick(null)}
                  className="btn btn-block mt-3 border border-gray-300 text-gray-600">
            ไม่ใช้คูปอง
          </button>
        )}
      </div>
    </div>
  );
}
